import { useContext, useCallback, useEffect, useRef, useState } from "react"
import { CompactSlotSelector, CustomModal } from "@components"
import { NotificationContext } from "@context"
import { guardianFullname, getGraphQLErrorMessage, mySqlToLocal, studentFullname } from "@helpers"
import { useInfiniteScroll } from "@heroui/use-infinite-scroll"

import {
  Autocomplete,
  AutocompleteItem,
  Button, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Select,
  SelectItem,
} from "@heroui/react";

import { GET_GROUP_BOOKING_MODAL_DATA } from "@graphql"
import { useQuery } from "@apollo/client"
import { debounce, sortBy, unionBy } from "lodash"
import PropTypes from 'prop-types'

const GroupBookingModal = ({ 
  defaultMeetingType,
  eventId, 
  groupId, 
  lessonId = null,
  handleBookSlot, 
  handleClose, 
  isSaving, 
  meetingTypes,
  selectedSlotId, 
  show, 
  initialSlots,
  title,
}) => {
  const [hasMadeBooking, setHasMadeBooking] = useState(false)
  const [haveAllRecordsLoaded, setHaveAllRecordsLoaded] = useState(false)
  const [page, setPage] = useState(1)
  const [paginatorInfo, setPaginatorInfo] = useState({})
  const [perPage] = useState(250)
  const [searchTerm, setSearchTerm] = useState("") 
  const [selectedGuardian, setSelectedGuardian] = useState(null)
  const [selectedMeetingType, setSelectedMeetingType] = useState(null)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(selectedSlotId ? initialSlots.find(slot => slot.id === selectedSlotId) || null : null)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const {notify} = useContext(NotificationContext)
  const [slots, setSlots] = useState(initialSlots)
  const [students, setStudents] = useState([])

  const studentAutocompleteRef = useRef(null)

  // needs to be here as used in useEffect
  const handleSelectGuardian = (selectedGuardianId) => {
    const guardian = selectedStudent.guardians.find(guardian => guardian.id === selectedGuardianId)

    if (hasGuardianBookingAtSelectedSlotsTime(guardian.booked_slots)) {
      setSelectedSlot(null)
    }

    setSelectedGuardian(guardian)
  }

  // needs to be here as used in useEffect
  const handleSelectLesson = (selectedLessonId) => {
    setSelectedLesson(selectedGuardian.lessons.find(lesson => lesson.id === selectedLessonId))
  }

  useEffect(() => {
    if (selectedStudent && selectedStudent.guardians.length === 1) {
      handleSelectGuardian(selectedStudent.guardians[0].id)
    }

    if (!selectedStudent || selectedStudent.guardians.length === 0) {
      setSelectedGuardian(null)
      setSelectedLesson(null)
    }
  }, [selectedStudent]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(selectedGuardian && selectedGuardian?.lessons.length === 1) {
      handleSelectLesson(selectedGuardian.lessons[0].id)
    }

    if (!selectedGuardian || selectedGuardian.lessons.length === 0) {
      setSelectedLesson(null)
    }
  }, [selectedGuardian]) // eslint-disable-line react-hooks/exhaustive-deps

  const studentsQuery = useQuery(GET_GROUP_BOOKING_MODAL_DATA, {
    variables: {
      input: {
        event_id: eventId,
        group_id: groupId,
        lesson_id: lessonId,
        search_term: searchTerm,
      },
      page,
      perPage,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    skip: haveAllRecordsLoaded,
    onCompleted: data => {
      const newPaginatorInfo = data.groupBookingStudentAvailability.paginatorInfo
      const newStudents = data.groupBookingStudentAvailability.data
      setPaginatorInfo(newPaginatorInfo)

      const mergedStudents = sortBy(
        unionBy(students, newStudents, "id"),
        ["forename", "surname"]
      );
      
      setStudents(newPaginatorInfo.currentPage === 1 ? newStudents : mergedStudents);

      if (!haveAllRecordsLoaded && mergedStudents.length === newPaginatorInfo.totalBeforeFilters) {
        setHaveAllRecordsLoaded(true);
      }
    },

    onError: errors => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while retrieving student data."), open: true })
  })
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedStudentSearch = useCallback(
    debounce((term) => {
      setSearchTerm(term)
      setPage(1)
    }, 500), []
  );

  const formatBookedSlots = () => {
    return selectedGuardian ? selectedGuardian.booked_slots.map(slot => mySqlToLocal(slot.starts_at)) : []
  }

  const hasGuardianBookingAtSelectedSlotsTime = (guardianBookedSlots) => {
    return selectedSlot ? guardianBookedSlots.some(guardianBookedSlot => selectedSlot.starts_at.isSame(guardianBookedSlot.starts_at)) : false
  }

  const handleLoadMore = () => {
    if (!studentsQuery.loading && paginatorInfo.hasMorePages) {
      setPage(page + 1)
    }
  }
  
  const handleSave = () => {
    const meetingTypeId = defaultMeetingType?.id 
      ?? selectedGuardian?.meeting_type_id 
      ?? selectedMeetingType?.id;    

      handleBookSlot(selectedSlot.id, selectedStudent.id, selectedGuardian.user_id, selectedLesson.id, meetingTypeId).then((response) => {
        if (!response.errors) {
          updateSlotStatus()
          setHasMadeBooking(true)      
          updateStudents()
          setSelectedStudent(null)
          setSelectedGuardian(null)
          setSelectedLesson(null)
          setSelectedSlot(null)
          setSelectedMeetingType(null)
          studentAutocompleteRef.current.focus()
        }
    })
  }

  const updateSlotStatus = () => {
    const index = slots.findIndex(slot => slot.id === selectedSlot.id)
    const newSlots = [...slots]
    newSlots[index] = {...newSlots[index], status: "Booked"}
    setSlots(newSlots)
  }

  const updateStudents = () => {
    
   setStudents((prevStudents) =>
    prevStudents
      // Map over each student to update the target student.
      .map((student) => {
        if (student.id !== selectedStudent.id) {
          return student;
        }

        // For the target student, update its guardians.
        const updatedGuardians = student.guardians
          .map((guardian) => {
            if (guardian.id !== selectedGuardian.id) {
              return guardian;
            }

            // For the target guardian, filter out the lesson.
            const updatedLessons = guardian.lessons.filter(
              (lesson) => lesson.id !== selectedLesson.id
            );

            // Optionally, if no lessons remain, you might want to remove this guardian.
            // For now, we return the guardian with the updated lessons.
            return { ...guardian, lessons: updatedLessons };
          })
          // Optionally, remove guardians that no longer have any lessons.
          .filter((guardian) => guardian.lessons.length > 0);

        return { ...student, guardians: updatedGuardians };
      })
      // Optionally, remove the student if they no longer have any guardians.
      .filter((student) => student.guardians.length > 0)
    )
  }
    
  const handleSelectMeetingType = (selectedMeetingTypeId) => {
    setSelectedMeetingType(meetingTypes.find(meetingType => meetingType.id === selectedMeetingTypeId))
  }

  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot)
  }

  const handleSelectStudent = (selectedStudentId) => {
    const newSelectedStudent = selectedStudentId ? students.find(student => student.id === selectedStudentId) : null
    setSelectedStudent(newSelectedStudent)
  }

  const handleStudentSearchTermChange = (searchTerm) => {
    if (!haveAllRecordsLoaded) {
      debouncedStudentSearch(searchTerm)
    }
  }
  
  const isMeetingTypeSelectVisible = () => {
    if (defaultMeetingType !== null) return false
    if (selectedGuardian === null) return false
    if (selectedGuardian.meeting_type_id !== null) return false

    return true
  }

  const isSubmitDisabled = () => {
    if(!selectedStudent || !selectedGuardian || !selectedLesson || !selectedSlot) return true
    
    if (!defaultMeetingType && !selectedMeetingType && !selectedGuardian.meeting_type_id) return true

    return false
  }

  const [, scrollerRef] = useInfiniteScroll({
    hasMore: paginatorInfo.hasMorePages,
    isEnabled: true,
    shouldUseLoader: false,
    onLoadMore: handleLoadMore,
  });

  const isDisabled = isSubmitDisabled()
  const isGuardiansDisabled = !selectedStudent || selectedStudent.guardians.length <= 1
  const isLessonsDisabled = !selectedGuardian || selectedGuardian.lessons.length <= 1
  const guardianBookedSlots = formatBookedSlots()
  const selectedGuardianKey = new Set(selectedGuardian ? [selectedGuardian.id] : [])
  const selectedLessonKey = new Set(selectedLesson ? [selectedLesson.id] : [])
  const selectedMeetingTypeKey = new Set(selectedMeetingType ? [selectedMeetingType.id] : [])
  const slotsWithRecentBookings = slots
  const showMeetingTypeSelect = isMeetingTypeSelectVisible()
  const showLessonSelector = !lessonId

  return (
    <CustomModal
      isOpen={show}
      size="xl"
      handleClose={() => handleClose(hasMadeBooking)}
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Group Bookings</ModalHeader>
          
            <ModalBody className="gap-1">
              <div className='mt-2 mb-3'>
                <h1 className="text-lg font-semibold">{title}</h1>
                <p className='text-md text-content2-foreground'>Select a student, guardian, lesson and slot to create a booking.</p>
              </div>

              <div className='flex flex-wrap gap-3'>
                <Autocomplete
                  isLoading={studentsQuery.loading}
                  defaultItems={students}
                  size="md"
                  scrollRef={scrollerRef}
                  label="Select a student"
                  placeholder="Type to search..."
                  onInputChange={handleStudentSearchTermChange}
                  onSelectionChange={(selectedOption) => handleSelectStudent(selectedOption)}
                  selectedKey={selectedStudent?.id ?? ""}
                  ref={studentAutocompleteRef}
                >
                  {(student) => (
                    <AutocompleteItem 
                      key={student.id} 
                      className="py-2 capitalize" 
                      textValue={`${studentFullname(student)}`}
                    >
                      <div className="flex items-center gap-2">
                        {studentFullname(student)}
                      </div>
                    </AutocompleteItem>
                  )}
                </Autocomplete>

                <Select 
                  label="Select a guardian" 
                  selectedKeys={selectedGuardianKey} 
                  onSelectionChange={(keys) => handleSelectGuardian([...keys][0])} 
                  isDisabled={isGuardiansDisabled}
                >
                  {selectedStudent?.guardians.map((guardian) => (
                    <SelectItem 
                      key={guardian.id} 
                      textValue={`${guardianFullname(guardian)}`}
                    >
                      {guardianFullname(guardian)}
                    </SelectItem>
                  ))}
                </Select>
                
                {showLessonSelector && (
                  <Select 
                    label="Select a lesson" 
                    selectedKeys={selectedLessonKey} 
                    onSelectionChange={(keys) => handleSelectLesson([...keys][0])} 
                    isDisabled={isLessonsDisabled}
                  >
                    {selectedGuardian && selectedGuardian?.lessons.map((lesson) => (
                      <SelectItem key={lesson.id} textValue={`${lesson.name}`}>
                        {lesson.name}
                      </SelectItem>
                    ))}
                  </Select>
                )}

                {showMeetingTypeSelect && (
                  <Select 
                    label="Select a meeting type" 
                    selectedKeys={selectedMeetingTypeKey} 
                    onSelectionChange={(keys) => handleSelectMeetingType([...keys][0])}
                  >
                    {meetingTypes.map((meetingType) => (
                      <SelectItem 
                        key={meetingType.id} 
                        textValue={`${meetingType.name}`}
                      >
                        {meetingType.name}
                      </SelectItem>
                    ))}
                  </Select>
                )}


                <CompactSlotSelector 
                  guardianBookedSlots={guardianBookedSlots}
                  handleSelectSlot={(slot) => handleSelectSlot(slot)} 
                  slots={slotsWithRecentBookings} 
                  selectedSlot={selectedSlot} 
                />
              </div>
            </ModalBody>

            <ModalFooter>
              <div className="flex justify-between w-full">
                <Button onPress={() => handleClose(hasMadeBooking)} tabIndex={-1}>Close</Button>
                <Button color="secondary" onPress={handleSave} tabIndex={-1} isDisabled={isDisabled} isLoading={isSaving}>Create Booking</Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}

GroupBookingModal.propTypes = {
  defaultMeetingType: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  eventId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  handleBookSlot: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  lessonId: PropTypes.string,
  meetingTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired
  ),
  selectedSlotId: PropTypes.string,
  show: PropTypes.bool.isRequired,
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      starts_at: PropTypes.object.isRequired,
      status: PropTypes.string.isRequired,
    }).isRequired
  ),
  title: PropTypes.string.isRequired,
} 

export default GroupBookingModal